import { useState } from "react";
import { motion } from "framer-motion";
import testimonialRaphael from "@/assets/testimonial-raphael-new.png";
import testimonialJacob from "@/assets/testimonial-jacob-new.png";
import testimonialAlex from "@/assets/testimonial-alex-new.png";


/** Témoignages réels déjà publiés sur le site actuel. Aucune citation modifiée. */
const jacob = {
  id: "jacob",
  name: "Jacob Saey-Williamson",
  role: "Courtier immobilier",
  company: "Courtier immobilier",
  image: testimonialJacob,
  focusLarge: "object-[center_28%]",
  short: "Maintenant, les gens me contactent directement grâce à ce qu'ils voient sur mes réseaux.",
};

const secondaries = [
  {
    id: "raphael",
    name: "Raphael Gagné",
    role: "Wisera Technologies",
    company: "Wisera Technologies",
    image: testimonialRaphael,
    focusLarge: "object-[center_22%]",
    focusSmall: "object-[center_20%]",
    short: "Ils ne font pas juste exécuter : ils proposent des idées créatives qui sortent du cadre.",
    quote:
      "Ce que j'aime avec l'Agence 8020, c'est qu'ils ne font pas juste exécuter : ils proposent des idées créatives qui sortent du cadre. Grâce à eux, Wisera a gagné en visibilité, et on reçoit maintenant des leads directement de notre contenu sur les réseaux sociaux.",
  },
  {
    id: "alex",
    name: "Alex Guennette",
    role: "Pilote NASCAR",
    company: "Athlex",
    image: testimonialAlex,
    focusLarge: "object-[center_30%]",
    focusSmall: "object-[center_28%]",
    short: "On arrive avec nos idées, ils reviennent avec un plan clair et structuré.",
    quote:
      "Ce que j'aime avec l'Agence 8020, c'est qu'ils sont à l'écoute. On arrive avec nos idées, et ils reviennent avec un plan de tournage clair, structuré, qui rend ça simple pour nous. On sait où on s'en va, et le résultat est toujours pro, sans se casser la tête.",
  },
];

type Secondary = (typeof secondaries)[number];

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

const TestimonialsV2 = () => {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <section id="resultats" aria-labelledby="temoignages-titre" className="scroll-mt-24 py-14 md:py-24">
      <div className="mx-auto max-w-[1280px] px-5 md:max-w-[1180px] md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-primary">Témoignages</p>
          <h2
            id="temoignages-titre"
            className="mt-4 max-w-[24ch] text-[1.7rem] font-semibold leading-[1.12] tracking-tight text-foreground md:mt-6 md:text-5xl"
          >
            Ils parlent de l'impact.
            <br />
            <span className="text-muted-foreground">Pas seulement des livrables.</span>
          </h2>
        </motion.div>

        <div className="mt-8 grid items-start gap-8 md:mt-20 md:grid-cols-[65fr_35fr] md:gap-14">

          {/* Témoignage principal — fixe */}
          <figure className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-secondary md:aspect-[16/11]">
            <img
              src={jacob.image}
              alt={`${jacob.name}, ${jacob.company}`}
              loading="eager"
              decoding="async"
              className={`absolute inset-0 h-full w-full object-cover ${jacob.focusLarge}`}
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-background via-background/70 to-transparent"
            />
            <div className="absolute inset-x-0 bottom-0 max-w-[36ch] p-6 md:p-8">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-primary">Témoignage</p>
              <blockquote className="mt-3 text-lg font-medium leading-[1.3] tracking-tight text-foreground md:text-2xl">
                « {jacob.short} »
              </blockquote>
              <p className="mt-4 text-sm font-medium text-foreground">{jacob.name}</p>
              <p className="text-sm text-muted-foreground">{jacob.role}</p>
            </div>
          </figure>

          {/* Colonne secondaire — accordéons */}
          <div className="flex flex-col gap-6 md:gap-8 md:pt-2">
            {secondaries.map((t) => (
              <SecondaryAccordion
                key={t.id}
                t={t}
                open={openId === t.id}
                onToggle={() => setOpenId((cur) => (cur === t.id ? null : t.id))}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const SecondaryAccordion = ({
  t,
  open,
  onToggle,
}: {
  t: Secondary;
  open: boolean;
  onToggle: () => void;
}) => {
  return (
    <motion.figure
      layout="position"
      transition={{ duration: 0.45, ease: EASE_OUT }}
      className={`group rounded-xl border-l transition-[background-color,border-color] duration-[450ms] ${
        open ? "border-primary/60 bg-secondary/40" : "border-transparent bg-transparent"
      }`}
      style={{ transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`Voir le témoignage complet de ${t.name}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className="w-full cursor-pointer rounded-xl px-4 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      >
        <div className="flex gap-5">
          <motion.img
            src={t.image}
            alt={`${t.name}, ${t.company}`}
            loading="eager"
            decoding="async"
            animate={{ scale: open ? 1.05 : 1 }}
            transition={{ duration: 0.45, ease: EASE_OUT }}
            className={`h-16 w-16 shrink-0 rounded-full object-cover will-change-transform md:h-20 md:w-20 ${t.focusSmall}`}
          />
          <div className="min-w-0">
            <blockquote className="text-sm leading-relaxed text-muted-foreground/80 transition-colors duration-[450ms] ease-out group-hover:text-foreground md:text-base">
              « {t.short} »
            </blockquote>
            <figcaption className="mt-3 text-sm">
              <span className="flex items-center gap-2 font-medium text-foreground">
                {t.name}
                <motion.span
                  aria-hidden="true"
                  animate={{ rotate: open ? 45 : 0, opacity: open ? 1 : 0.6 }}
                  transition={{ duration: 0.4, ease: EASE_OUT }}
                  className="inline-block text-xs text-primary"
                >
                  +
                </motion.span>
              </span>
              <span className="block text-muted-foreground/70">{t.role}</span>
            </figcaption>
          </div>
        </div>

        {/* Contenu révélé — s'ouvre vers le bas */}
        <motion.div
          initial={false}
          animate={open ? "open" : "closed"}
          variants={{
            open: { height: "auto", opacity: 1 },
            closed: { height: 0, opacity: 0 },
          }}
          transition={{
            height: { duration: 0.42, ease: EASE_OUT },
            opacity: { duration: open ? 0.35 : 0.2, ease: EASE_OUT, delay: open ? 0.08 : 0 },
          }}
          className="overflow-hidden"
        >
          <motion.div
            variants={{
              open: { transition: { staggerChildren: 0.06, delayChildren: 0.08 } },
              closed: { transition: { staggerChildren: 0, staggerDirection: -1 } },
            }}
            className="pt-5"
          >
            <motion.div
              variants={{
                open: { opacity: 1, y: 0, scale: 1 },
                closed: { opacity: 0, y: 8, scale: 0.99 },
              }}
              transition={{ duration: 0.42, ease: EASE_OUT }}
              className="overflow-hidden rounded-lg will-change-transform"
            >
              <img
                src={t.image}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                className={`aspect-[4/3] w-full object-cover ${t.focusLarge}`}
              />
            </motion.div>
            <motion.blockquote
              variants={{
                open: { opacity: 1, y: 0 },
                closed: { opacity: 0, y: 8 },
              }}
              transition={{ duration: 0.4, ease: EASE_OUT }}
              className="mt-4 text-sm leading-relaxed text-muted-foreground"
            >
              « {t.quote} »
            </motion.blockquote>
            <motion.div
              variants={{
                open: { opacity: 1, y: 0 },
                closed: { opacity: 0, y: 8 },
              }}
              transition={{ duration: 0.4, ease: EASE_OUT }}
            >
              <p className="mt-3 text-sm font-medium text-foreground">{t.name}</p>
              <p className="text-sm text-muted-foreground">{t.role}</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </motion.figure>
  );
};


export default TestimonialsV2;
